import { useEffect, useRef } from 'react';

/**
 * useFocusTrap — WAI-ARIA dialog modal odak yönetimi.
 *
 * - `active` true iken Tab / Shift+Tab tuşlarını `ref.current` içinde döngüye sokar.
 * - ESC tuşunda `onEscape` callback'i (varsa) çalıştırılır.
 * - Aktif olduğu sürece ilk odaklanabilir öğeye odak verir; kapanınca
 *   önceki odaklanmış öğeye (çağıran buton vb.) odağı geri verir.
 *
 * Referans: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 *
 * @param {boolean} active   Trap aktif mi (modal açık mı?)
 * @param {React.RefObject} ref Modal paneline bağlı ref
 * @param {{ onEscape?: () => void }} [options]
 */
export default function useFocusTrap(active, ref, options = {}) {
  const { onEscape } = options;
  const onEscapeRef = useRef(onEscape);
  useEffect(() => { onEscapeRef.current = onEscape; }, [onEscape]);

  useEffect(() => {
    if (!active || !ref || !ref.current) return undefined;

    const node = ref.current;
    const previouslyFocused =
      typeof document !== 'undefined' ? document.activeElement : null;

    const FOCUSABLE_SELECTOR = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(',');

    const getFocusable = () => {
      const nodes = node.querySelectorAll(FOCUSABLE_SELECTOR);
      return Array.from(nodes).filter(
        el => !el.hasAttribute('disabled') && el.tabIndex !== -1
      );
    };

    // İlk odaklanabilir öğeye odak ver
    const focusables = getFocusable();
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      // Fallback: panelin kendisini odakla
      if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
      node.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && typeof onEscapeRef.current === 'function') {
        e.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const list = getFocusable();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || !node.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    node.addEventListener('keydown', handleKeyDown);

    return () => {
      node.removeEventListener('keydown', handleKeyDown);
      // Önceki odağı geri yükle
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch (_) { /* no-op */ }
      }
    };
  }, [active, ref]);
}
